import { useEffect, useRef, useState } from 'react'
import type { CampaignDownloadCapabilityState, CampaignFile } from '@/domain/campaign.types'
import { CAMPAIGN_FILE_ASSOCIATION_BATCH_LIMIT } from '@/domain/campaign.types'
import { isCampaignError } from '@/domain/campaign-errors'
import type {
	CampaignBulkDownloadScope,
	CampaignFileDiscoveryCandidate,
	CampaignUploadInput,
	CampaignUploadOutcome,
	CampaignUploadTransientState,
} from '@/domain/campaign-ports'
import {
	useAssociateCampaignFiles,
	useCampaignDownloadCapabilities,
	useCampaignFileDiscovery,
	useCampaignFiles,
	useDownloadCampaignBulk,
	useDownloadCampaignFile,
	useRetryCampaignUploadAssociation,
	useUploadAndAssociate,
} from '@/hooks/useCampaigns'
import { useThumbnails } from '@/hooks/useThumbnails'

/**
 * Builds the picker's confirm/retry handler: a no-op when the selection is
 * empty or over the batch limit; otherwise submits, and on success resets the
 * picker's own state (close, clear search, clear selection) and the mutation's
 * error/success state.
 */
function makeSubmitAssociation(
	associationMutation: ReturnType<typeof useAssociateCampaignFiles>,
	selectedIds: ReadonlySet<string>,
	isDisabled: boolean,
	resetPickerState: () => void,
) {
	return () => {
		if (isDisabled) return
		associationMutation.mutate([...selectedIds], {
			onSuccess: () => {
				resetPickerState()
				associationMutation.reset()
			},
		})
	}
}

// The Campaign Files-section container's data-composition layer — the sole
// consumer of the Files/discovery/association/upload/download hooks. Split
// into three focused sub-hooks (gallery, picker, upload) so each stays short;
// `pages/Campaigns/CampaignDetail.tsx` renders from what the combined hook
// returns, feeding it down into `CampaignFileGallery`,
// `CampaignFilePickerDialog`, `CampaignUploadDialog`, and `CampaignDownloadMenu`.

/**
 * Bulk-download state and handlers, split out from `useCampaignGallerySelection`
 * so each hook stays small — mirrors this file's existing multi-hook
 * composition (`useCampaignFilePickerState`, `useCampaignUploadState`).
 * `Download selected` and `Download all assets` share one lock: activating one
 * while the other is pending is a no-op, mirroring `downloadingFileId`'s own
 * single-slot convention. Fully independent of that individual-download lock.
 * The backend re-validates every id authoritatively; the client-side gate here
 * only decides whether a button is enabled and what to send, never a security
 * boundary. Selection is preserved after either success or failure.
 */
function useCampaignBulkDownload(
	campaignId: string,
	fileList: readonly CampaignFile[],
	selectedFileIds: ReadonlySet<string>,
	downloadCapabilities: CampaignDownloadCapabilityState,
) {
	const [bulkDownloadKind, setBulkDownloadKind] = useState<'selected' | 'all' | null>(null)
	const [bulkDownloadError, setBulkDownloadError] = useState<string | null>(null)
	const bulkDownloadMutation = useDownloadCampaignBulk()

	function runBulkDownload(kind: 'selected' | 'all', scope: CampaignBulkDownloadScope) {
		setBulkDownloadKind(kind)
		setBulkDownloadError(null)
		bulkDownloadMutation.mutate(
			{ campaignId, scope },
			{
				onError: (error) =>
					setBulkDownloadError(
						isCampaignError(error) ? error.message : 'Unable to download these Files. Please try again.',
					),
				onSettled: () => setBulkDownloadKind(null),
			},
		)
	}

	function onDownloadSelected() {
		if (bulkDownloadKind !== null || downloadCapabilities.selected !== 'available') return
		const eligibleIds = fileList.filter((file) => selectedFileIds.has(file.fileId)).map((file) => file.fileId)
		if (eligibleIds.length === 0) return
		runBulkDownload('selected', { kind: 'selected', fileIds: eligibleIds })
	}

	function onDownloadAll() {
		if (bulkDownloadKind !== null || downloadCapabilities.all !== 'available' || fileList.length === 0) return
		runBulkDownload('all', { kind: 'all' })
	}

	return { bulkDownloadKind, bulkDownloadError, onDownloadSelected, onDownloadAll }
}

function useCampaignGallerySelection(campaignId: string) {
	const [selectedFileIds, setSelectedFileIds] = useState<ReadonlySet<string>>(new Set())
	const [downloadingFileId, setDownloadingFileId] = useState<string | null>(null)
	const files = useCampaignFiles(campaignId)
	const fileList = files.data ?? []
	// Resolved once here for the whole collection, then handed down as props — the
	// gallery and its cards stay hook-free. The shared hook is consumed unchanged.
	const thumbnailUrls = useThumbnails(fileList)
	const downloadCapabilities = useCampaignDownloadCapabilities(campaignId)
	const downloadMutation = useDownloadCampaignFile()
	const bulkDownload = useCampaignBulkDownload(campaignId, fileList, selectedFileIds, downloadCapabilities)

	function toggleFileSelection(fileId: string) {
		setSelectedFileIds((current) => {
			const next = new Set(current)
			if (next.has(fileId)) next.delete(fileId)
			else next.add(fileId)
			return next
		})
	}

	/**
	 * One in-flight URL request at a time PER FILE: a second click on the same
	 * File while its request is pending is ignored, while every other File's
	 * control stays clickable. Cleared once settled either way, so the next
	 * explicit click requests a fresh URL — none is ever cached.
	 */
	function onDownloadIndividual(fileId: string) {
		if (downloadingFileId === fileId) return
		setDownloadingFileId(fileId)
		downloadMutation.mutate(fileId, { onSettled: () => setDownloadingFileId(null) })
	}

	return {
		files: fileList,
		isFilesLoading: files.isLoading,
		isFilesError: files.isError,
		selectedFileIds,
		toggleFileSelection,
		selectionCount: selectedFileIds.size,
		downloadCapabilities,
		downloadingFileId,
		onDownloadIndividual,
		thumbnailUrls,
		...bulkDownload,
	}
}

/**
 * Excludes candidates already associated with the Campaign and applies the
 * local filename search — trim, case-insensitive substring match. While the
 * associated-Files result has not yet resolved, exclusion is a no-op (never
 * "exclude everything"). Every remaining candidate is eligible: nothing else
 * makes an HTTP-backed candidate ineligible in this MVP.
 */
function visiblePickerCandidates(
	rawCandidates: readonly CampaignFileDiscoveryCandidate[],
	associatedFileIds: ReadonlySet<string> | undefined,
	search: string,
): CampaignFileDiscoveryCandidate[] {
	const trimmedSearch = search.trim().toLowerCase()
	return rawCandidates
		.filter((candidate) => associatedFileIds === undefined || !associatedFileIds.has(candidate.file.fileId))
		.map((candidate) => ({ file: candidate.file, eligible: true }))
		.filter((candidate) => trimmedSearch === '' || candidate.file.fileName.toLowerCase().includes(trimmedSearch))
}

function useCampaignFilePickerState(campaignId: string) {
	const [isPickerOpen, setIsPickerOpen] = useState(false)
	const [pickerSearch, setPickerSearch] = useState('')
	const [pickerSelectedIds, setPickerSelectedIds] = useState<ReadonlySet<string>>(new Set())

	const associatedFiles = useCampaignFiles(campaignId)
	const discovery = useCampaignFileDiscovery()
	const associationMutation = useAssociateCampaignFiles(campaignId)

	function togglePickerSelection(fileId: string) {
		setPickerSelectedIds((current) => {
			const next = new Set(current)
			if (next.has(fileId)) next.delete(fileId)
			else next.add(fileId)
			return next
		})
	}

	const isSelectionOverLimit = pickerSelectedIds.size > CAMPAIGN_FILE_ASSOCIATION_BATCH_LIMIT
	const isConfirmDisabled = pickerSelectedIds.size === 0 || isSelectionOverLimit
	const submitAssociation = makeSubmitAssociation(associationMutation, pickerSelectedIds, isConfirmDisabled, () => {
		setIsPickerOpen(false)
		setPickerSearch('')
		setPickerSelectedIds(new Set())
	})

	return {
		isPickerOpen,
		onOpenPicker: () => {
			setPickerSelectedIds(new Set())
			setIsPickerOpen(true)
		},
		onClosePicker: () => setIsPickerOpen(false),
		pickerCandidates: visiblePickerCandidates(
			discovery.data ?? [],
			associatedFiles.data === undefined ? undefined : new Set(associatedFiles.data.map((file) => file.fileId)),
			pickerSearch,
		),
		isPickerLoading: discovery.isLoading,
		isPickerError: discovery.isError,
		pickerSearch,
		onPickerSearchChange: setPickerSearch,
		pickerSelectedIds,
		togglePickerSelection,
		isPickerSelectionOverLimit: isSelectionOverLimit,
		isConfirmDisabled,
		onConfirmPicker: submitAssociation,
		onRetryPickerAssociation: submitAssociation,
		isAssociationPending: associationMutation.isPending,
		isAssociationError: associationMutation.isError,
		isAssociationSuccess: associationMutation.isSuccess,
	}
}

/**
 * Owns the one AbortController per in-flight full upload attempt — it is never
 * exposed through the port. Closing the DIALOG aborts nothing (the dialog only
 * hides; this container stays mounted); only this container unmounting —
 * navigating away from the Campaign — aborts the active direct PUT. After that,
 * `isMounted` suppresses the resulting outcome, since there is nowhere to report
 * it. Returns a fresh signal per attempt and a guarded outcome reporter.
 */
function useUploadAbortOwnership() {
	const abortRef = useRef<AbortController | null>(null)
	const isMountedRef = useRef(true)
	useEffect(() => {
		isMountedRef.current = true
		return () => {
			isMountedRef.current = false
			abortRef.current?.abort()
			abortRef.current = null
		}
	}, [])

	return {
		// A stale controller from a settled attempt is dropped, not aborted: this
		// is a new attempt, and the previous one already finished.
		startAttempt: (): AbortSignal => {
			const controller = new AbortController()
			abortRef.current = controller
			return controller.signal
		},
		settle: (): boolean => {
			abortRef.current = null
			return isMountedRef.current
		},
	}
}

function useCampaignUploadState(campaignId: string) {
	const [isUploadOpen, setIsUploadOpen] = useState(false)
	const [uploadTransientState, setUploadTransientState] = useState<CampaignUploadTransientState | 'idle'>('idle')
	const [uploadOutcome, setUploadOutcome] = useState<CampaignUploadOutcome | undefined>(undefined)
	const [uploadProgress, setUploadProgress] = useState<number | null>(null)

	const uploadMutation = useUploadAndAssociate(campaignId)
	const retryMutation = useRetryCampaignUploadAssociation(campaignId)
	const abort = useUploadAbortOwnership()

	function reportOutcome(outcome: CampaignUploadOutcome) {
		if (abort.settle()) setUploadOutcome(outcome)
	}

	function startUpload(file: CampaignUploadInput) {
		setUploadOutcome(undefined)
		setUploadProgress(null)
		const signal = abort.startAttempt()
		const options = { onTransientState: setUploadTransientState, onProgress: setUploadProgress, signal }
		uploadMutation.mutate({ file, options }, { onSuccess: reportOutcome })
	}

	function retryUploadAssociation(operationToken: string) {
		retryMutation.mutate(
			{ operationToken, options: { onTransientState: setUploadTransientState } },
			{ onSuccess: reportOutcome },
		)
	}

	/**
	 * Clears the container-owned upload PRESENTATION only — transient state,
	 * outcome, and progress. It does not open or close the dialog, start an
	 * upload, abort one, or otherwise touch the mutation. Opening a fresh dialog
	 * and the dialog's own deliberate reset paths share this one implementation, so
	 * the two can never drift apart.
	 */
	function resetUploadPresentation() {
		setUploadTransientState('idle')
		setUploadOutcome(undefined)
		setUploadProgress(null)
	}

	return {
		isUploadOpen,
		onOpenUpload: () => {
			resetUploadPresentation()
			setIsUploadOpen(true)
		},
		// Reached only from the dialog's deliberate user paths — Upload another File,
		// Remove after an upload failure, and a replacement selection. Never from a
		// progress callback, and never while an operation is pending; selection is
		// locked during an association failure, so that outcome cannot be hidden
		// through picker or drop interaction.
		onResetUploadPresentation: resetUploadPresentation,
		onCloseUpload: () => setIsUploadOpen(false),
		onStartUpload: startUpload,
		onRetryUploadAssociation: retryUploadAssociation,
		uploadTransientState,
		uploadOutcome,
		uploadProgress,
		isUploadPending: uploadMutation.isPending || retryMutation.isPending,
	}
}

export function useCampaignFilesSectionContainer(campaignId: string) {
	return {
		...useCampaignGallerySelection(campaignId),
		...useCampaignFilePickerState(campaignId),
		...useCampaignUploadState(campaignId),
	}
}
